import { ConnectedAccountStatus, CreateActivatedChallengeRequest, CreateBallotRequest, CreatePaywallRoleAssignmentRequest, CreatePointRequest, PaymentContentDraft, PaymentTargetType, PurchaseItemRequest, StripePendingTransactionRequest } from '../../types';
import { emptyPost, get, post, throwHttpError } from './api';

import { getMainPage } from '../page-name';
import { isDefined } from '../utils';

const API_BASE_URL = '/api';

/**
 * Initiate Stripe Connect onboarding or get dashboard link for an organization
 */
export async function getStripeOnboardingOrDashboardLink(
    organizationId: string,
    organizationKey?: string
): Promise<{ url: string }> {
    const params = new URLSearchParams();
    if (organizationKey) {
        params.append('organization_key', organizationKey);
    }

    const url = `${API_BASE_URL}/payment_account/setup?organization_id=${organizationId}&${params.toString()}`;
    const response = await fetch(url, emptyPost());

    if (!response.ok) {
        throw new Error(`Failed to initiate Stripe onboarding: ${response.statusText}`);
    }

    return await response.json();
}

export async function loadStripeStatus(organizationId: string, organizationKey?: string): Promise<ConnectedAccountStatus> {

    const parameters = new URLSearchParams();
    parameters.append('organization_id', organizationId);
    if (organizationKey) {
        parameters.append('organization_key', organizationKey);
    }

    try {
        const response = await fetch(`/api/payment_account/status?${parameters.toString()}`, get());

        if (response.ok) {
            return await response.json();
        } else if (response.status === 401) {
            // Organization doesn't have a connect account yet
            return {
                account_id: '',
                status: 'not_connected',
                charges_enabled: false,
                payouts_enabled: false,
                details_submitted: false,
                onboarding_completed: false,
                dashboard_url: ''
            };
        } else {
            throw new Error('Failed to load account status');
        }
    } catch (error) {
        console.error('Failed to load account status:', error);
        // Return default status for missing account instead of undefined
        return {
            account_id: '',
            status: 'not_connected',
            charges_enabled: false,
            payouts_enabled: false,
            details_submitted: false,
            onboarding_completed: false,
            dashboard_url: ''
        };
    }
}

export async function startStripeCheckoutSession(
    email: string,
    products: PurchaseItemRequest[],
    organization_id: string,
    target_entity_id: string,
    payment_target_type: PaymentTargetType,
    draft_Content: CreatePointRequest | CreateActivatedChallengeRequest | CreateBallotRequest | CreatePaywallRoleAssignmentRequest | undefined,
    existing_entity_id: string | undefined,
    read_key: string | undefined,
    edit_key: string | undefined
): Promise<{ session_id: string; url: string }> {
    const params = new URLSearchParams();
    if (read_key) {
        params.append('read_key', read_key);
    }

    if (edit_key) {
        params.append('edit_key', edit_key);
    }

    let content_draft: PaymentContentDraft;
    let existing_entity_id_local: string | undefined;

    if (!existing_entity_id) {
        const content_draft_inner = (() => {
            switch (payment_target_type) {
                case PaymentTargetType.Form:
                    return { Form: draft_Content as CreatePointRequest };
                case PaymentTargetType.ActivatedChallenge:
                    return { ActivatedChallenge: draft_Content as CreateActivatedChallengeRequest };
                case PaymentTargetType.Ballot:
                    return { Ballot: draft_Content as CreateBallotRequest };
                case PaymentTargetType.Paywall:
                    return { Paywall: draft_Content as CreatePaywallRoleAssignmentRequest };
                default:
                    throw new Error('Unsupported PaymentTargetType');
            }
        })();
        if (!isDefined(content_draft_inner)) {
            getMainPage().errorToastElement?.showToast('An error occurred while starting the checkout session. Please contact the map administrator if the problem persists. Additional info: content draft is undefined.');
        }
        content_draft = {
            New: content_draft_inner
        };
        existing_entity_id_local = undefined;
    } else {
        const existingContent = (() => {
            switch (payment_target_type) {
                case PaymentTargetType.Form:
                    return { Form: draft_Content as CreatePointRequest };
                case PaymentTargetType.ActivatedChallenge:
                    return { ActivatedChallenge: { $oid: existing_entity_id } };
                case PaymentTargetType.Ballot:
                    return { Ballot: { $oid: existing_entity_id } };
                case PaymentTargetType.Paywall:
                    return { Paywall: { $oid: existing_entity_id } };
                default:
                    throw new Error('Unsupported PaymentTargetType');
            }
        })();
        content_draft = { Existing: existingContent };
        existing_entity_id_local = existing_entity_id;
        // if existing_entity_id_local is undefined send error
        if (!isDefined(existing_entity_id_local)) {
            getMainPage().errorToastElement?.showToast('An error occurred while starting the checkout session. Please contact the map administrator if the problem persists. Additional info: existing entity id is undefined.');
        }
    }

    const body: StripePendingTransactionRequest = {
        email,
        purchase_items: products,
        target_entity_id,
        content: content_draft,
        existing_entity_id: existing_entity_id_local
    };

    const url = `${API_BASE_URL}/payment_account/new_purchase?organization_id=${organization_id}&${params.toString()}`;

    const response = await fetch(url, post(body));
    if (!response.ok) {
        getMainPage().errorToastElement?.showToast('An error occurred while starting the checkout session. Please contact the map administrator if the problem persists.');
        await throwHttpError('An error occurred while starting the checkout session.', response);
    }

    return await response.json();
}